<template>
    <div class="transcription-feedback-action">
        <button @click="toggleLike" :class="[
            'icon-button',
            liked ? 'active' : ''
        ]">
            <span class="material-symbols-outlined">thumb_up</span>
        </button>
        <button @click="toggleDislike" :class="[
            'icon-button dislike',
            disliked ? 'active' : ''
        ]">
            <span class="material-symbols-outlined">thumb_down</span>
        </button>
    </div>
</template>

<script setup>
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { showToast } from '@/utils/swal';
import { handleError, ref, watch } from 'vue';

const loader = useLoaderStore();

// Props from parent component
const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    attempt: Object,
});

const emit = defineEmits(['saved']);

const liked = ref(false)
const disliked = ref(false)

async function toggleLike() {
    try {
        loader.show();

        const { data } = await api.post(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/attempt-feedback`, {
            attemptid: props.attempt.id,
            feedback_type: !liked.value ? 'correct' : 'none',
        });        

        liked.value = !liked.value
        if (liked.value) disliked.value = false

        emit('saved', data?.data ? {
            feedback_type: data.data.feedback_type,
            description: data.data.description,
            metadata: data.data.metadata,
            status: data.data.status,
        } : null);

        showToast({
            icon: 'success',
            title: 'Success! ',
            text: 'Transcribe feedback saved successfully.',
        });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

async function toggleDislike() {
    try {
        loader.show();

        const { data } = await api.post(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/attempt-feedback`, {
            attemptid: props.attempt.id,
            feedback_type: !disliked.value ? 'incorrect' : 'none',
        });

        disliked.value = !disliked.value
        if (disliked.value) liked.value = false

        emit('saved', data?.data ? {
            feedback_type: data.data.feedback_type,
            description: data.data.description,
            metadata: data.data.metadata,
            status: data.data.status,
        } : null);

        showToast({
            icon: 'success',
            title: 'Success! ',
            text: 'Transcribe feedback saved successfully.',
        });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

watch(() => props.attempt, (newAttempt) => {
    if (newAttempt) {
        liked.value = newAttempt?.feedback?.feedback_type === 'correct'
        disliked.value = newAttempt?.feedback?.feedback_type === 'incorrect'
    }
}, { immediate: true, deep: true });
</script>

<style scoped>
.transcription-feedback-action {
    display: flex;
    align-items: center;
    gap: 0;
    background-color: #f8f9fa;
    border: 1px solid #ddd;
    border-radius: 30px;
    padding: 0;
    width: 73px;
}

.tippy-content .transcription-feedback-action {
    margin-top: 20px;
    margin-bottom: 10px;
}

.transcription-feedback-action .icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 35px;
    width: 35px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 14px;
    color: #888;
    transition: color 0.2s ease, background-color 0.2s ease;
    border-radius: 50%;
    padding: 6px;
}

.transcription-feedback-action .icon-button span {
    font-size: 18px;
}

.transcription-feedback-action .icon-button:hover {
    color: #000;
    background-color: #eee;
}

.transcription-feedback-action .icon-button.active {
    background-color: #e0f7e9;
    color: #1a7f37;
}
.transcription-feedback-action .icon-button.dislike.active {
    background-color: #ffebee;
    color: #d32f2f;
}
</style>
